import { useState } from 'react';
import { Box } from '@mui/material';
import { compact, uniq } from 'lodash';
import { PageCard } from '../../../../Compounds/CardWithTitle';
import { useSnackbar } from '../../../../../Context/SnackbarContext';
import { DEFAULT_ADVANCE_BUTTON_STYLE } from '../../../../../utils/styleHelpers';
import { updateWizardData } from '../../../../../store/ProvisionalAsset';
import { StyledTextArea } from '../../../../Atoms/StyledTextArea';

interface IdentifyRPMAccountStepProps {
  nextStep: () => void;
  title: string;
}

export const IdentifyRPMAccountStep = ({ nextStep, title }: IdentifyRPMAccountStepProps) => {
  const [isValidStep, setIsValidStep] = useState<boolean>(false);
  const [value, setValue] = useState('');
  const { setSnack } = useSnackbar();

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    setValue(text);

    const invalidDelimiter = /,|;|\t| /g.test(text);
    if (invalidDelimiter) {
      setIsValidStep(false);
      setSnack({ message: 'Invalid Delimiter', type: 'error', open: true });
      return;
    }

    const lines = text.split('\n').filter((l) => l.trim() !== '');
    const invalidId = lines.some((line) => !/^\d+$/.test(line));

    if (invalidId) {
      setIsValidStep(false);
      setSnack({ message: 'IDs must contain only digits', type: 'error', open: true });
      return;
    }
    if (lines.length === 0) {
      setIsValidStep(false);
      setSnack({ message: 'Please enter at least one ID', type: 'error', open: true });
      return;
    }

    setIsValidStep(true);
    setSnack({ message: 'Valid Step', type: 'success', open: true });
  };

  const submitStep = () => {
    if (!isValidStep) return;

    const ids = uniq(
      compact(
        value
          .split('\n')
          .map((line) => line.trim())
          .filter((line) => /^\d+$/.test(line))
          .map(Number)
      )
    );
    updateWizardData({ rpmAccountIds: ids, assetMapping: {} });
    nextStep();
  };

  const cardActions = () => (
    <button
      className={DEFAULT_ADVANCE_BUTTON_STYLE}
      onClick={submitStep}
      data-cy="next-step-button"
      disabled={!isValidStep}
      data-testid="next-step-button">
      Next
    </button>
  );

  return (
    <PageCard
      dataTestId="identify-rpm-account-step"
      dataCy="identify-rpm-account-step"
      childrenWrapperClass="max-w-[60vw] min-w-full"
      title={title}
      cardActions={cardActions()}>
      <Box
        sx={{
          flex: 1,
          p: 2
        }}>
        <StyledTextArea
          isFocused
          maxRows={15}
          placeholder="Paste RPM Account IDs here separated by line breaks."
          value={value}
          onChange={handleChange}
          borderStyle="dashed"
          placeholderAlign="center"
          margin="40px 0 0 0"
        />
      </Box>
    </PageCard>
  );
};
